/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Card, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import ConfirmModal from "../../components/confirmModal/confirmModel";
import {
  deleteTableBookingLayout,
  fetchTableBookingLayouts,
} from "../../api/tableBookingApi";
import { showToast } from "../../store/features/toast/toastSlice";
import CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML";
import {
  countAreas,
  countSlots,
  layoutRecordId,
  normalizeLayoutsList,
} from "./tableBookingUtils";
import "./tableBooking.css";

const TableBookingLayoutsList = () => {
  const { t } = useTranslation();
  const { clubId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [layouts, setLayouts] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const res = await fetchTableBookingLayouts(clubId);
      setLayouts(normalizeLayoutsList(res));
    } catch (err) {
      dispatch(
        showToast({
          message: typeof err === "string" ? err : err?.message || t("TABLE_BOOKING_LAYOUTS_LOAD_ERROR"),
          type: "error",
        })
      );
      setLayouts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [clubId]);

  const confirmDelete = async () => {
    const row = deleteTarget;
    if (!row || !clubId) return;
    const id = layoutRecordId(row);
    if (!id) return;
    setDeleting(true);
    try {
      await deleteTableBookingLayout(clubId, id);
      dispatch(
        showToast({
          message: t("TABLE_BOOKING_LAYOUT_DELETED"),
          type: "success",
        })
      );
      setDeleteTarget(null);
      load();
    } catch (err) {
      dispatch(
        showToast({
          message:
            typeof err === "string"
              ? err
              : err?.message || t("TABLE_BOOKING_LAYOUT_DELETE_ERROR"),
          type: "error",
        })
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card className="mainCard mainCardWrapper">
        <Card.Body>
          <div className="d-flex flex-wrap gap-2 mb-4">
            <Link
              to={`/clubs/${clubId}/table-booking/layouts/new`}
              className="btn btn-primary fillButton"
            >
              <i className="bx bxs-plus-circle" /> {t("TABLE_BOOKING_ADD_LAYOUT")}
            </Link>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={load}
              disabled={loading}
            >
              {t("REFRESH")}
            </button>
          </div>
          {loading ? (
            <CustomLoadingHTML />
          ) : layouts.length === 0 ? (
            <p className="text-muted mb-0">{t("TABLE_BOOKING_NO_LAYOUTS")}</p>
          ) : (
            <div className="table-responsive table-booking-layouts-scroll">
              <Table hover className="mb-0 align-middle table-booking-layouts-table">
                <thead>
                  <tr>
                    <th>{t("TABLE_BOOKING_COL_TITLE")}</th>
                    <th>{t("TABLE_BOOKING_COL_VIEWKEY")}</th>
                    <th>{t("TABLE_BOOKING_COL_SORT")}</th>
                    <th>{t("TABLE_BOOKING_COL_AREAS")}</th>
                    <th>{t("TABLE_BOOKING_COL_SLOTS")}</th>
                    <th>{t("TABLE_BOOKING_COL_TZ")}</th>
                    <th>{t("ACTIONS")}</th>
                  </tr>
                </thead>
                <tbody>
                  {layouts.map((row) => {
                    const id = layoutRecordId(row);
                    return (
                      <tr key={id || row.viewkey}>
                        <td>{row.title}</td>
                        <td>
                          <code>{row.viewkey}</code>
                        </td>
                        <td>{row.sortorder ?? row.sortOrder ?? "—"}</td>
                        <td>{countAreas(row)}</td>
                        <td>{countSlots(row)}</td>
                        <td>{row.timezone || "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 me-2"
                            onClick={() =>
                              navigate(
                                `/clubs/${clubId}/table-booking/layouts/${id}`
                              )
                            }
                          >
                            {t("EDIT")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0"
                            onClick={() => setDeleteTarget(row)}
                          >
                            {t("DELETE")}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      <ConfirmModal
        modalTitle={t("TABLE_BOOKING_DELETE_LAYOUT_TITLE")}
        modalDescription={t("TABLE_BOOKING_DELETE_LAYOUT_DESC")}
        isUnderline={false}
        showModal={Boolean(deleteTarget)}
        leftBtnTitle={t("DELETE")}
        isModelIcon={true}
        iconColor={"mdi mdi-delete"}
        iconCricle={"iconCricleDelete"}
        leftBtnClass={"ok_Btn"}
        isFirstButton={true}
        isSecondButton={true}
        rightBtnClass="borderButton"
        rightBtnTitle={t("NO_CANCEL")}
        onProcced={confirmDelete}
        rightButtonClick={() => setDeleteTarget(null)}
        leftdisabled={deleting}
        loaderButtonShow={deleting}
      />
    </>
  );
};

export default TableBookingLayoutsList;
